import {
  Box,
  CircularProgress,
  Container,
  Grid2,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import {
  LocationOn,
  Apartment,
  People,
  Phone,
  Restaurant,
  Bathtub,
  Security,
  EventNote,
  Comment,
  Business,
  Home,
  Map,
  Place,
  Male,
  Female,
  Wc,
  AutoStories,
  Work,
  Group,
  Groups,
} from "@mui/icons-material";

import ImageGrid from "../../components/properties/ImageGrid";
import LocationMap from "../../components/properties/LocationMap";
// import Booking from "../../components/properties/Booking";
import NotFound from "../../pages/NotFound";
import { useParams } from "react-router-dom";
import { useGetMyPropertyByIdQuery } from "./propertiesApiSlice";
import { useEffect, useState } from "react";
import ActionButtons from "../../components/properties/ActionButtons";
import PropertyLocation from "../../components/properties/PropertyLocation";
import UnitGrid from "../../components/units/UnitGridPG";
import UserDetails from "../../components/users/UserDetails";
import PropertyLifestyle from "../../components/properties/PropertyLifestyle";

function MyPropertyDetails() {
  const { id } = useParams();
  const [propertyState, setPropertyState] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  const {
    // Getting data and renaming it to properties
    data: property,
    // refetch,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetMyPropertyByIdQuery(id);

  // Gender Logic
  const getGenderConfig = (gender) => {
    // const g = gender?.toLowerCase();
    if (gender === "male")
      return {
        label: "Men's PG",
        color: "info.main",
        icon: <Male />,
      };
    // return { label: "Men's PG", icon: <Male /> };
    if (gender === "female")
      return {
        label: "Women's PG",
        color: "#cf5e93",
        icon: <Female />,
      };
    return {
      label: "Co-ed (Separate Floors)",
      color: "inherit",
      icon: <Groups />,
    };
  };

  const genderConfig = getGenderConfig(property?.gender);

  // TenantType Logic
  const getTenantType = (tenantType) => {
    // const g = tenantType?.toLowerCase();
    if (tenantType === "students")
      return {
        label: "Students",
        color: "success.main",
        icon: <AutoStories />,
      };
    if (tenantType === "professionals")
      return {
        label: "Professionals",
        color: "success.main",
        icon: <Work />,
      };
    return {
      label: "Students & Professionals",
      color: "success.main",
      icon: <Group />,
    };
  };

  const tenantTypeConfig = getTenantType(property?.tenantType);

  // useEffect(() => {
  //   // Trigger a refetch when this page is navigated to after property edit
  //   refetch();
  // }, [refetch]);

  // Update propertyState when property data is available
  useEffect(() => {
    if (isSuccess) {
      setPropertyState(property); // Set properties when data is fetched
    }
  }, [isSuccess, property]);

  if (!isLoading && property === undefined) {
    return <NotFound />;
  }

  let content;

  if (isLoading || propertyState === null)
    content = (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (isError) {
    content = <p className="errmsg">{error?.data?.message}</p>;
  }

  if (isSuccess) {
    if (propertyState !== null) {
      content = (
        <>
          {/* Header */}
          <Container sx={{ marginTop: 5, marginBottom: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" }, // Column for small screens, row for larger
                alignItems: { xs: "center", sm: "center" }, // Center-align for small screens
                justifyContent: { sm: "space-between" }, // Space-between for larger screens
                gap: { xs: 2, sm: 0 }, // Add vertical spacing between buttons on small screens
                textAlign: { xs: "center", sm: "left" }, // Center-align text on small screens
              }}
            >
              <Box>
                <Typography variant="h4" component="h1">
                  {property.title}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 1,
                    justifyContent: { xs: "center", sm: "flex-start" },
                    textAlign: { xs: "center", sm: "left" },
                  }}
                >
                  {/* <Chip
                    icon={<Apartment />}
                    label={property.propertyTypeName}
                    color="primary"
                    variant="outlined"
                  /> */}
                  <Chip
                    label={genderConfig.label}
                    // color="secondary"
                    sx={{
                      color: genderConfig.color,
                      borderColor: genderConfig.color,
                      "& .MuiChip-icon": {
                        color: genderConfig.color, // Ensures the icon matches the text/border
                      },
                    }}
                    icon={genderConfig.icon}
                    variant="outlined"
                    // sx={{ fontWeight: "bold" }}
                  />
                  <Chip
                    // icon={<Work />}
                    label={tenantTypeConfig.label}
                    // color="success"
                    sx={{
                      color: tenantTypeConfig.color,
                      borderColor: tenantTypeConfig.color,
                      "& .MuiChip-icon": { color: tenantTypeConfig.color },
                      pl: 0.5,
                    }}
                    icon={tenantTypeConfig.icon}
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Action Buttons */}
              <ActionButtons
                property={property}
                propertyState={propertyState}
                setPropertyState={setPropertyState}
              />
            </Box>
          </Container>

          {/* Image Grid */}
          <Container sx={{ marginBottom: 5 }}>
            <ImageGrid data={property} />
          </Container>

          {/* Property Units */}
          <UnitGrid property={propertyState ?? property} />

          {/* Lifestyle (Amenities & Rules) */}
          <PropertyLifestyle
            amenities={propertyState?.amenities || property?.amenities}
            // amenities={propertyState?.houseRules || property?.houseRules}
            rules={propertyState?.houseRules || property?.houseRules}
          />

          {/* Property Location */}
          <Container sx={{ marginBottom: 2 }}>
            <PropertyLocation data={property} />
          </Container>

          <Container sx={{ marginBottom: 5 }}>
            <Grid2 container size={{ xs: 12 }} spacing={3}>
              <Grid2 size={{ xs: 12 }}>
                <LocationMap data={property} />
              </Grid2>

              {/* <Grid2 size={{ xs: 12, md: 4 }}>
                <Booking data={property} />
              </Grid2> */}
            </Grid2>
          </Container>

          {/* Owner Details */}
          <UserDetails property={property} />

          {/* <Container>
            <Grid2 container gap={2}>
              <Grid2>
                <Box paddingY={2}>
                  <Typography variant="h1">Heading 1</Typography>
                  <Typography variant="h2">Heading 2</Typography>
                  <Typography variant="h3">Heading 3</Typography>
                  <Typography variant="h4">Heading 4</Typography>
                  <Typography variant="h5">Heading 5</Typography>
                  <Typography variant="h6">Heading 6</Typography>
                  <Typography variant="subtitle1">subtitle1</Typography>
                  <Typography variant="subtitle2">subtitle2</Typography>
                  <Typography variant="body2">Body text1</Typography>
                  <Typography variant="body2">Body text2</Typography>
                  <Typography variant="button">Button text</Typography>
                  <br />
                  <Typography variant="caption">Caption text</Typography>
                </Box>
              </Grid2>
            </Grid2>
          </Container> */}
        </>
      );
    }
  }

  return content;
}

export default MyPropertyDetails;
